/**
 * Push notifications (iOS/native). Registers the device's APNs token with
 * Supabase (`device_tokens`) so the `send-push` edge function can reach
 * the signed-in user. No-op on web — the browser build has no APNs.
 *
 * Call registerPush(userId) once the user is signed in on native, and
 * unregisterPush(userId) on sign-out so pushes don't follow the next
 * account that signs in on the same phone.
 *
 * Requires (native side, done once):
 *   - the "Push Notifications" capability + `aps-environment` entitlement
 *     on the iOS App target, and
 *   - APNs auth-key secrets on the send-push function
 *     (APNS_KEY, APNS_KEY_ID, APNS_TEAM_ID, APNS_BUNDLE_ID).
 */
import { Capacitor } from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'
import { supabase } from './supabase'
import { requestRoomNav, requestScreenNav, type RoomGame } from './navigationBus'

// The 'registration' listener is wired once per app launch, but the
// signed-in user can change (sign out / in). Keep the current id in a
// module ref the listener reads, so a fresh token always lands on the
// account that's actually signed in.
let listenersWired = false
let currentProfileId: string | null = null
/** This device's APNs token, once iOS has handed it over. */
let currentToken: string | null = null

export async function registerPush(profileId: string): Promise<void> {
  if (Capacitor.getPlatform() === 'web') return
  currentProfileId = profileId

  try {
    let receive = (await PushNotifications.checkPermissions()).receive
    if (receive === 'prompt' || receive === 'prompt-with-rationale') {
      receive = (await PushNotifications.requestPermissions()).receive
    }
    if (receive !== 'granted') return

    if (!listenersWired) {
      listenersWired = true

      // Await listener registration BEFORE register(): iOS can hand back a
      // cached APNs token synchronously, and an un-awaited addListener may
      // not be wired on the native side in time to catch it — which drops
      // the token and the device never lands in device_tokens.
      await PushNotifications.addListener('registration', async (token) => {
        currentToken = token.value
        if (!currentProfileId) return
        // Upsert this device's token for the current user. RLS lets a user
        // write only their own rows; the unique (profile_id, token) key
        // makes a re-register a no-op refresh. Cast through `any` —
        // device_tokens isn't in the generated types yet.
        //
        // This MUST be awaited (or otherwise have .then() called on it).
        // supabase-js query builders are lazy thenables: the HTTP request
        // is only issued from inside .then(). `void builder` never calls
        // it, so the upsert silently went nowhere and device_tokens sat
        // empty for every user from July 5 to Aug 17, 2026, which is why
        // no push of any kind was delivered in that window. The July 4
        // debug build had a .then(dbg) chained on, which is the only
        // reason registration ever looked confirmed.
        try {
          const { error } = await (supabase as any).from('device_tokens').upsert(
            { profile_id: currentProfileId, token: token.value, platform: Capacitor.getPlatform() },
            { onConflict: 'profile_id,token' },
          )
          if (error) console.warn('[push] token upsert failed', error)
        } catch (e) {
          console.warn('[push] token upsert threw', e)
        }
      })

      await PushNotifications.addListener('registrationError', (err) => {
        console.warn('[push] registration error', err)
      })

      // Foreground receipt: iOS suppresses the banner while the app is
      // open, so this is where an in-app toast would go if we want one.
      await PushNotifications.addListener('pushNotificationReceived', (n) => {
        console.log('[push] received (foreground):', n.title)
      })

      // Tap: route to where the push points.
      //  - room-scoped ("your turn") → that match. App.tsx validates the room
      //    is still live before entering and falls back to the hub if dead.
      //  - trophy ("my story won") → the profile page (where trophies live).
      //  - weekly opens / voting opens / story got votes / reminders → the
      //    weekly landing.
      //  - anything else / unknown → just open the app.
      await PushNotifications.addListener('pushNotificationActionPerformed', (a) => {
        const data = (a.notification?.data ?? {}) as {
          game?: string; room_id?: string; type?: string
        }
        const game = data.game
        if (data.room_id && (game === 'iet' || game === 'colorful' || game === 'three_step')) {
          requestRoomNav({ game: game as RoomGame, roomId: data.room_id })
        } else if (data.type === 'my_story_won') {
          requestScreenNav('profile')
        } else if (
          data.type === 'my_story_voted' ||
          data.type === 'weekly_event_open' ||
          data.type === 'weekly_event_close' ||
          data.type === 'weekly_reminder'
        ) {
          // Reminders (weekly entries and the dailies) both live on the
          // Weekly tab, so one destination covers every scope.
          requestScreenNav('weekly')
        }
      })
    }

    await PushNotifications.register()
  } catch (e) {
    console.warn('[push] registerPush failed', e)
  }
}

/**
 * Release this device from the account that is signing out, so its
 * pushes stop following the phone to whoever signs in next. Must run
 * while the session is still valid (RLS lets a user delete only their
 * own rows). Scoped to this device's token when we know it, so signing
 * out on a phone leaves the same account's iPad alone; without a
 * known token it falls back to every row for the profile.
 */
export async function unregisterPush(profileId: string): Promise<void> {
  currentProfileId = null
  if (Capacitor.getPlatform() === 'web') return
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let q = (supabase as any).from('device_tokens').delete().eq('profile_id', profileId)
    if (currentToken) q = q.eq('token', currentToken)
    const { error } = await q
    if (error) console.warn('[push] unregisterPush failed', error)
  } catch (e) {
    console.warn('[push] unregisterPush threw', e)
  }
}
